import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { CATEGORIES, formatCurrencyByCountry } from '../lib/constants'
import { supabase } from '../lib/supabase'
import { useAuth } from '../context/AuthContext'
import FoundingListerBadge from './FoundingListerBadge'
import ListingPlaceholder from './ListingPlaceholder'

function formatMemberSince(createdAt) {
if (!createdAt) return null
const date = new Date(createdAt)
const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
return `${months[date.getMonth()]} ${date.getFullYear()}`
}

export default function ListingCard({ listing, distanceKm, isAnyFallback, isFavourited: isFavouritedProp }) {
const navigate = useNavigate()
const { user } = useAuth()
const cat = CATEGORIES.find(c => c.id === listing.category_id)
const [isFavourited, setIsFavourited] = useState(isFavouritedProp ?? false)
const [loading, setLoading] = useState(false)

useEffect(() => {
if (!user) return
if (isFavouritedProp !== undefined) {
setIsFavourited(isFavouritedProp)
return
}
checkFavourite()
}, [user, listing.id])

const checkFavourite = async () => {
const { data } = await supabase
.from('favourites')
.select('id')
.eq('user_id', user.id)
.eq('listing_id', listing.id)
.maybeSingle()
setIsFavourited(!!data)
}

const toggleFavourite = async (e) => {
e.stopPropagation()
if (!user) { navigate(`/auth?reason=favourite&next=${encodeURIComponent(window.location.pathname + window.location.search)}`); return }
setLoading(true)
if (isFavourited) {
await supabase.from('favourites').delete().eq('user_id', user.id).eq('listing_id', listing.id)
setIsFavourited(false)
} else {
await supabase.from('favourites').insert({ user_id: user.id, listing_id: listing.id })
setIsFavourited(true)
}
setLoading(false)
}

const handleOwnerClick = (e) => {
e.stopPropagation()
if (listing.owner_id) navigate(`/user/${listing.owner_id}`)
}

const memberSince = formatMemberSince(listing.profiles?.created_at)
const ownerName = listing.profiles?.full_name || 'Anonymous'
const ownerInitial = ownerName !== 'Anonymous' ? ownerName[0].toUpperCase() : '?'

return (
<div className="card listing-card" onClick={() => navigate(`/listing/${listing.id}`)}>
<div className="listing-img" style={{ position: 'relative' }}>
{listing.images?.[0]
? <img src={listing.images[0]} alt={listing.title} loading="lazy" style={{ width:'100%', height:180, objectFit:'cover', objectPosition:'center' }} />
: <ListingPlaceholder listing={listing} />
}

{user && (
<button
onClick={toggleFavourite}
disabled={loading}
style={{
position: 'absolute', top: 8, right: 8,
background: 'rgba(255,255,255,0.9)',
border: 'none', borderRadius: '50%',
width: 32, height: 32,
display: 'flex', alignItems: 'center', justifyContent: 'center',
cursor: 'pointer', fontSize: 18,
boxShadow: '0 1px 4px rgba(0,0,0,0.2)'
}}
>
{isFavourited ? '❤️' : '🤍'}
</button>
)}
</div>
<div className="card-body">
<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
<span className={`badge ${listing.available ? 'badge-green' : 'badge-amber'}`}>
{listing.available ? 'Available' : 'Hired out'}
</span>
{listing.includes_operator && <span className="badge badge-accent" style={{ fontSize: 10 }}>🛠️ Includes operator</span>}
{cat && <span style={{ fontSize: 12, color: 'var(--text-faint)' }}>{cat.icon} {cat.name}{distanceKm !== null && distanceKm !== undefined && distanceKm < 50 ? ` · ${distanceKm.toLocaleString()}km` : ''}</span>}
</div>
<div className="listing-title">{listing.title}</div>
<div className="listing-meta" style={{ marginBottom: 8 }}>
{listing.suburb}, {listing.state}{listing.postcode ? ` · ${listing.postcode}` : ''}
</div>
<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
<div>
{(() => {
const { price_per_hour: hr, price_per_day: day, price_per_week: wk, country } = listing
const primary = day > 0
? { amount: day, unit: '/day' }
: hr > 0
? { amount: hr, unit: '/hr' }
: wk > 0
? { amount: wk, unit: '/wk' }
: null
const extras = []
if (primary?.unit !== '/hr' && hr > 0) extras.push(`${formatCurrencyByCountry(hr, country)}/hr`)
if (primary?.unit !== '/day' && day > 0) extras.push(`${formatCurrencyByCountry(day, country)}/day`)
if (primary?.unit !== '/wk' && wk > 0) extras.push(`${formatCurrencyByCountry(wk, country)}/wk`)
return (
<>
<div className="listing-price">
{primary
? <>{formatCurrencyByCountry(primary.amount, country)}<span style={{ fontSize: 13, fontWeight: 400, color: 'var(--text-muted)' }}>{primary.unit}</span></>
: <span style={{ fontSize: 14, color: 'var(--text-muted)' }}>Ask owner</span>
}
</div>
{extras.length > 0 && (
<div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{extras.join(' · ')}</div>
)}
</>
)
})()}
</div>
{listing.profiles && (
((listing.profiles?.review_count || 0) > 0 && listing.profiles?.rating != null && listing.profiles?.rating > 0) ? (
<div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: 'var(--text-muted)' }}>
<span style={{ color: '#EF9F27' }}>★</span>
<span>{listing.profiles.rating.toFixed(1)}</span>
<span>({listing.profiles.review_count})</span>
</div>
) : (
<div style={{ fontSize: 12, color: 'var(--text-faint)' }}>New</div>
)
)}
</div>
{listing.deposit > 0 && (
<div style={{ fontSize: 12, color: 'var(--text-faint)', marginTop: 4 }}>
+ {formatCurrencyByCountry(listing.deposit, listing.country)} deposit
</div>
)}

{listing.owner_id && (
<div
onClick={handleOwnerClick}
style={{
marginTop: 8,
paddingTop: 8,
borderTop: '1px solid var(--border)',
display: 'flex',
alignItems: 'center',
gap: 8,
cursor: 'pointer',
transition: 'background 0.15s',
padding: '8px 6px 0',
borderRadius: 6,
}}
onMouseEnter={e => e.currentTarget.style.background = 'var(--gray-50)'}
onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
>
<div style={{
width: 24,
height: 24,
borderRadius: '50%',
background: listing.profiles?.avatar_url ? 'transparent' : 'var(--primary-light)',
display: 'flex',
alignItems: 'center',
justifyContent: 'center',
overflow: 'hidden',
flexShrink: 0,
}}>
{listing.profiles?.avatar_url ? (
<img src={listing.profiles.avatar_url} alt={ownerName} loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
) : (
<span style={{ fontSize: 11, fontWeight: 700, color: 'var(--primary)' }}>{ownerInitial}</span>
)}
</div>
<div style={{ fontSize: 12, color: 'var(--primary)', fontWeight: 600, flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
{ownerName}
</div>
<FoundingListerBadge profile={listing.profiles} size="sm" />
<span style={{ fontSize: 12, color: 'var(--text-faint)' }}>›</span>
</div>
)}

{memberSince && (
<div style={{ fontSize: 11, color: 'var(--text-faint)', marginTop: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
🛡️ Member since {memberSince}
</div>
)}
</div>
</div>
)
}